import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument } from 'angularfire2/firestore';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/observable/combineLatest';
import { Subject } from 'rxjs/Subject';
import * as firebase from 'firebase/app';
import { Query } from '@firebase/firestore-types'
import { UserService } from '../auth/_services/user.service'
import { HttpClient } from '@angular/common/http';
import { Upload } from './upload';
import { forkJoin } from "rxjs/observable/forkJoin";
import { User } from "../auth/user";

interface Venue { // data should is collected for 'public' /venues path on the db and duplicated to be nested on each booking when saving a booking
    id?: string;
    name: string;
    city: string;
    country: string;
    // content: string;
    raId: number;
    address: string;
    capacity: number;
    website: string;
    phoneNumber: string;
}

interface Artist {
    id?: string;
    artistName: string;
    currentlyEdited: string[];
    bookings: any[];
    agencyId: string;
    biography: string;
    city: string;
    country: {};
    contactDetails: string;
    facebookPage: string;
    gwWebsite: {};
    socials: {};
}

interface Booking {
    id?: string;
    actType: string,
    agencyId: string;
    artistRef: string; // ??
    artist: Artist; // ?? Optional: Cloud functions should write the updated artist object into Booking document (via artistRef) on every write
    city: string,
    country: any;
    date: string;
    eventName: string;
    feeAmount: string;
    feeCurrency: string;
    image: string,
    legacyId: string; // ID for migrated Bookings from previous DB (MySQL)
    notes: string;
    showLength: string,
    showTime: string;
    soundcheckTime: string,
    status: string,
    venue: Venue;
    contacts: Contact[];
    flights: {}[];
    transportation: {}[];
    editHistory: {}[];  // Should display which user this object and when
    currentlyEdited: string[]; // What will the best to store the currently edited user (for displaying to other users if they are in edit mode on this object ?
}

interface Contact {
    id?: string,
    fullName: string;
    email: string;
    company: string;
    // affiliation: string;
    // role: string;
    // image: string;
    // gender: string;
    mobilePhoneNum: string;
    address: string;
    city: string,
    country: string,
    state: string,
    // birthday: string,
    // tags: string[],
    facebookProfile: string;
    notes: string;
    // createdBy?: string; // optional
    // editHistory: {}[];  // Should display which user this object and when
}

@Injectable()
export class AfsDatabaseServiceService {
    currentUser: User;
    O: Observable<any>;
    artists: Artist[] = [];
    bookings: Booking[] = [];
    bookingQuery: AngularFirestoreCollection<Booking>;
    query$ = new Subject<string>();
    venues$: Observable<{}[]>;
    venues: Observable<Venue[]>;
    user: Observable<User>;

    countryFilter$: BehaviorSubject<string | null>;
    nameFilter$: BehaviorSubject<string | null>;
    countries: any;

    bookingsPath: string; // Path to Bookings in DB
    artistsCol: AngularFirestoreCollection<any>;
    contactsCol: AngularFirestoreCollection<any>;
    bookingsCol: AngularFirestoreCollection<any>;
    venuesCol: AngularFirestoreCollection<Venue>;
    uploadCol: AngularFirestoreCollection<any>;

    constructor(
        private afs: AngularFirestore,
        private userService: UserService,
        private http: HttpClient
    ) {

        // Get list of Countries to be used by the forms
        this.http.get('../../assets/data/countries-full.json').subscribe(data => {
            this.countries = data;
        });

        const userId = userService.userId;
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));


        if (this.userService.userLevel >=10) {
            this.bookingsPath = 'probookings';
        }

        else {
            this.bookingsPath = 'bookings';
        }

        this.artistsCol = this.afs.collection('artists');
        this.contactsCol = this.afs.collection('contacts');
        this.bookingsCol = this.afs.collection(this.bookingsPath);
        this.venuesCol = this.afs.collection('venues');
        this.uploadCol = this.afs.collection('uploads/files/' + this.userService.userId);

        this.countryFilter$ = new BehaviorSubject(null); // For Country Typeahead Filter
        this.nameFilter$ = new BehaviorSubject(null); // For Venue Name Typeahead Filter

        this.venues$ = Observable.combineLatest(
            this.countryFilter$,
            this.nameFilter$
        ).switchMap(([country, name]) =>
            afs.collection('venues', ref => {
                let query: Query = ref;
                if (country) { query = query.where('country', '==', country) }
                if (name) { query = query.where('name', '==', name) }
                return query;
            }).valueChanges()
            );
    }

    // Create a File Details for Uploads in DB
    saveUploadFile(upload: Upload) {
        console.debug('Upload : ', upload);
        let data = {
            name: upload.name,
            url: upload.url,
            progress: upload.progress,
            createdAt: upload.createdAt
        };
        return this.uploadCol.add(data);
    }

    // Get Artist via Id
    getArtist(id) {
        console.debug('AFS Service: Getting Artist: ' + id);
        let docRef = this.artistsCol.doc(id);
        return docRef.snapshotChanges();
    }

    // Get All Artists for this User's Agency Id
    getArtists() {
        console.debug('AFS Service: Getting Artists');
        let agencyId = this.userService.user.agencyId;
        return this.afs.collection('artists/', ref => ref.where('agencyId', '==', agencyId).orderBy('artistName'))
            .snapshotChanges()
            .map(actions => {
                return actions.map(action => {
                    const data = action.payload.doc.data() as Artist;
                    const id = action.payload.doc.id;
                    return { id, ...data };
                });
            });
    }

    // Create a new Artist
    createArtist(data) {
        data.agencyId = this.userService.user.agencyId;
        data.managedBy = { [this.userService.user.uid]: true };
        return this.artistsCol.add(data);
    }

    // Get All Contacts for this User's Agency Id
    getContacts(filter) {
        console.debug('AFS Service: Getting Contacts');
        let agencyId = this.userService.user.agencyId;
        return this.afs.collection('contacts/', ref => ref.where('agencyId', '==', agencyId).orderBy('fullName'))
            .snapshotChanges()
            .map(actions => {
                return actions.map(action => {
                    const data = action.payload.doc.data() as Contact;
                    const id = action.payload.doc.id;
                    return { id, ...data };
                });
            });
    }

    // Update an existing Artist
    updateArtist(id, data) {
        data.agencyId = this.userService.user.agencyId;
        return this.artistsCol.doc(id).update(data);
    };

    // Delete Artist
    deleteArtist(id) {
        return this.artistsCol.doc(id).delete();
    };

    // Get All Bookings
    getBookings(filter) {
        console.debug('Getting Bookings... Path: (' + this.bookingsPath + ')');
        let bookingsPath = this.bookingsPath;
        let agencyId = this.userService.user.agencyId;

        // Get Artist Data in order to make sure the Artist Object in every Booking received is updated (doesn't write to DB unless DB booking updated)
        this.getArtists().subscribe(data => {
            console.debug('Got Artists for Bookings');
            this.artists = data;
        });

        if (!filter) {
            this.bookingQuery = this.afs.collection(bookingsPath);
        }

        if (filter) {
            let now = (new Date()).toISOString().slice(0, 10);
            switch (filter) {
                case 'future': { // Queries for Future Bookings
                    console.info('filtering future bookings');
                    this.bookingQuery = this.afs.collection(bookingsPath, ref =>
                        ref.where('date', '>=', now).orderBy("date", "asc")
                            .where('agencyId', '==', agencyId)

                    );
                    console.debug('This Booking Query Result: ', this.bookingQuery);
                    break;
                }

                case 'past': { // Queries for Past Bookings
                    console.info('filtering past bookings');
                    this.bookingQuery = this.afs.collection(bookingsPath, ref =>
                        ref.where('date', '<', now).orderBy("date", "desc")
                            .where('agencyId', '==', agencyId)
                    );
                    console.debug('This Booking Query Result: ', this.bookingQuery);
                    break;
                }

                case 'all': { // // Queries for All Bookings
                    console.debug('getting all bookings');
                    let artistId = 'AqrR1xFC33gisUfoF2b6';
                    this.bookingQuery = this.afs.collection(bookingsPath, ref => ref.where('agencyId', '==', agencyId));
                    break;
                }
            }
        }

        return this.bookingQuery
            .snapshotChanges()
            .map(actions => {
                return actions.map(action => {
                    console.debug('Booking : ', action.payload.doc.data());
                    const data = action.payload.doc.data() as Booking;
                    const id = action.payload.doc.id;

                    // Updates the Artist Data in each Booking according to Artist ID (doesn't write to DB unless booking is updated)
                    this.artists.forEach(a => {
                        if (a.id == data.artist.id) {
                            data.artist = a;
                            console.debug('Got Fresh Artist Data for Booking...', data);
                        }
                    });

                    return { id, ...data };
                });
            });
    }

    // Get Booking via Id
    getBooking(id) {
        let docRef = this.bookingsCol.doc(id);
        return docRef.snapshotChanges();
    };

    // Create a New Booking
    createBooking(data) {
        data.agencyId = this.userService.user.agencyId;
        data.managedBy = { [this.userService.user.uid]: true };
        return this.bookingsCol.add(data);
    }

    // Updates an existing Booking
    updateBooking(id, data) {
        data.agencyId = this.userService.user.agencyId;
        return this.bookingsCol.doc(id).update(data);
    };

    // Delete Booking
    deleteBooking(id) {
        return this.bookingsCol.doc(id).delete();
    };

    // Create a Contact
    createContact(data) {
        data.agencyId = this.userService.user.agencyId;
        data.managedBy = { [this.userService.user.uid]: true };
        return this.contactsCol.add(data);
    }

    // Update an existing Contact
    updateContact(id, data) {
        data.agencyId = this.userService.user.agencyId;
        return this.contactsCol.doc(id).update(data);
    };

    // Delete Booking
    deleteContact(id) {
        return this.contactsCol.doc(id).delete();
    };

    getAllVenues() {
        return this.venuesCol.snapshotChanges().map(actions => {
            return actions.map(action => {
                const data = action.payload.doc.data() as Venue;
                const id = action.payload.doc.id;
                console.debug('All Venues : ', data);
                return { id, ...data };
            });
        });
    }

    getVenues(start, end): any {
        return this.afs.collection('venues', ref =>
            ref.orderBy('name')
                .limit(5)
                .startAt(start)
                .endAt(end)
        );
    }

    filterByCountry(country: string | null) {
        console.debug('Seraching for venues in ' + country);
        this.countryFilter$.next(country);
        console.debug('Venue Filter Result : ', this.venues$);
        return this.venues$;
    }

    filterByName(name: string | null) {
        console.debug('Seraching for venues : ' + name);
        this.nameFilter$.next(name);
        console.debug('Venue Filter Result : ', this.venues$);
        return this.venues$;
    }
}



// WEBPACK FOOTER //
// ./src/app/_services/afs-database-service.service.ts